import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { 

  BookOpen, 
  Settings, 
  BarChart3, 
  Target, 
  Activity,
  FileText,
  TrendingUp,
  Calendar,
  AlertCircle,
  Plus,
  Download,
  Edit,
  Trash2,
  Eye
} from 'lucide-react'
import { 
  mockStrategies, 
  mockBacktestResults, 
  mockLiveTracking, 
  mockNotes,

  mockManualTestTrades 
} from '../utils/mockData'
import Navigation from '../components/Navigation'
import { LineChart, Line, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts'

export default function DemoStrategyDetailPage() {
  const { strategyId } = useParams<{ strategyId: string }>()
  const navigate = useNavigate()
  const [activeTab, setActiveTab] = useState('basic')
  const [strategy, setStrategy] = useState<any>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (strategyId) {
      const foundStrategy = mockStrategies.find(s => s.id === strategyId)
      setStrategy(foundStrategy)
      setLoading(false)
    }
  }, [strategyId])

  const tabs = [
    { id: 'basic', name: '基本信息', icon: BookOpen },
    { id: 'logic', name: '策略逻辑', icon: FileText },
    { id: 'params', name: '参数配置', icon: Settings },
    { id: 'backtest', name: '回测结果', icon: BarChart3 },
    { id: 'manual', name: '手动测试', icon: Target },
    { id: 'notes', name: '研究笔记', icon: FileText },
    { id: 'live', name: '实盘追踪', icon: Activity },
  ]

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'live':
        return 'bg-green-500/20 text-green-400 border border-green-500/30'
      case 'test':
        return 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/30'
      default:
        return 'bg-gray-500/20 text-gray-400 border border-gray-500/30'
    }
  }

  const getStatusLabel = (status: string) => {
    const statusMap: Record<string, string> = {
      draft: '草稿',
      test: '验证中',
      live: '实盘中'
    }
    return statusMap[status] || status
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-900 via-blue-800 to-indigo-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-400"></div>
      </div>
    )
  }

  if (!strategy) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-900 via-blue-800 to-indigo-900 flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="mx-auto h-16 w-16 text-red-400 mb-4" />
          <h3 className="text-xl font-medium text-white mb-2">策略不存在</h3>
          <p className="text-blue-200 mb-6">请检查策略ID是否正确</p>
          <button
            onClick={() => navigate('/demo')}
            className="bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 text-white font-medium py-2 px-4 rounded-xl transition-all duration-200"
          >
            返回首页
          </button>
        </div>
      </div>
    )
  }

  const renderTabContent = () => {
    switch (activeTab) {
      case 'basic':
        return (
          <div className="space-y-6">
            <div className="card">
              <h3 className="text-lg font-semibold text-ocean-primary mb-4">策略基本信息</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-ocean-secondary mb-2">策略名称</label>
                  <p className="text-ocean-primary">{strategy.name}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-ocean-secondary mb-2">策略类型</label>
                  <p className="text-ocean-primary">{strategy.type}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-ocean-secondary mb-2">交易市场</label>
                  <p className="text-ocean-primary">{strategy.market}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-ocean-secondary mb-2">交易对</label>
                  <p className="text-ocean-primary">{strategy.market_symbol}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-ocean-secondary mb-2">时间周期</label>
                  <p className="text-ocean-primary">{strategy.timeframe}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-ocean-secondary mb-2">状态</label>
                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(strategy.status)}`}>
                    {getStatusLabel(strategy.status)}
                  </span>
                </div>
              </div>
              <div className="mt-6">
                <label className="block text-sm font-medium text-ocean-secondary mb-2">策略描述</label>
                <p className="text-ocean-primary">{strategy.description}</p>
              </div>
            </div>
          </div>
        )

      case 'logic':
        return (
          <div className="space-y-6">
            <div className="card">
              <h3 className="text-lg font-semibold text-ocean-primary mb-4">策略逻辑</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-ocean-secondary mb-2">入场规则</label>
                  <p className="text-ocean-primary">{strategy.entry_rules || '均线金叉做多'}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-ocean-secondary mb-2">出场规则</label>
                  <p className="text-ocean-primary">{strategy.exit_rules || '均线死叉平仓'}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-ocean-secondary mb-2">风险管理</label>
                  <p className="text-ocean-primary">{strategy.risk_management || '单次最大亏损1%'}</p>
                </div>
              </div>
            </div>
          </div>
        )

      case 'params':
        return (
          <div className="space-y-6">
            <div className="card">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-ocean-primary">参数配置</h3>
                <button className="btn-primary flex items-center">
                  <Plus className="w-4 h-4 mr-2" />
                  添加参数
                </button>
              </div>
              <div className="space-y-4">
                <div className="border border-white/20 rounded-xl p-4">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="text-ocean-primary font-medium">移动平均线周期</h4>
                    <div className="flex items-center space-x-2">
                      <button className="text-violet-300 hover:text-violet-400">
                        <Edit className="w-4 h-4" />
                      </button>
                      <button className="text-red-300 hover:text-red-400">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                  <p className="text-ocean-secondary text-sm mb-2">短期移动平均线周期</p>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-ocean-secondary">默认值:</span>
                      <span className="text-ocean-primary ml-2">20</span>
                    </div>
                    <div>
                      <span className="text-ocean-secondary">范围:</span>
                      <span className="text-ocean-primary ml-2">5-100</span>
                    </div>
                  </div>
                </div>
                <div className="border border-white/20 rounded-xl p-4">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="text-white font-medium">止损比例</h4>
                    <div className="flex items-center space-x-2">
                      <button className="text-blue-300 hover:text-white">
                        <Edit className="w-4 h-4" />
                      </button>
                      <button className="text-red-300 hover:text-red-400">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                  <p className="text-blue-200 text-sm mb-2">止损百分比</p>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-blue-200">默认值:</span>
                      <span className="text-white ml-2">2%</span>
                    </div>
                    <div>
                      <span className="text-blue-200">范围:</span>
                      <span className="text-white ml-2">0.5%-10%</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )

      case 'backtest':
        return (
          <div className="space-y-6">
            <div className="card">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-semibold text-ocean-primary">回测结果</h3>
                <div className="flex space-x-2 sm:space-x-3">
                  <button className="btn-secondary flex items-center">
                    <Download className="w-4 h-4 mr-2" />
                    <span className="hidden sm:inline">导出报告</span>
                  </button>
                  <button className="btn-primary flex items-center justify-center" title="新建回测">
                    <Plus className="w-4 h-4 mr-2 sm:mr-2" />
                    <span className="hidden sm:inline">新建回测</span>
                  </button>
                </div>
              </div>
              
              {mockBacktestResults.find(b => b.strategy_id === strategyId) ? (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
                    <div className="bg-white/5 rounded-xl p-4 border border-white/10">
                      <div className="text-center">
                        <div className="text-2xl font-bold text-green-400">20.5%</div>
                        <div className="text-sm text-ocean-secondary">总收益率</div>
                      </div>
                    </div>
                    <div className="bg-white/5 rounded-xl p-4 border border-white/10">
                      <div className="text-center">
                        <div className="text-2xl font-bold text-red-400">-12.3%</div>
                        <div className="text-sm text-ocean-secondary">最大回撤</div>
                      </div>
                    </div>
                    <div className="bg-white/5 rounded-xl p-4 border border-white/10">
                      <div className="text-center">
                        <div className="text-2xl font-bold text-blue-400">60%</div>
                        <div className="text-sm text-ocean-secondary">胜率</div>
                      </div>
                    </div>
                    <div className="bg-white/5 rounded-xl p-4 border border-white/10">
                      <div className="text-center">
                        <div className="text-2xl font-bold text-purple-400">20</div>
                        <div className="text-sm text-ocean-secondary">交易次数</div>
                      </div>
                    </div>
                  </div>

                  {/* 图表分析 */}
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <div className="card">
                      <h3 className="text-lg font-semibold text-ocean-primary mb-4">收益曲线</h3>
                      <ResponsiveContainer width="100%" height={300}>
                        <LineChart data={[
                          { month: '1月', return: 5.2, benchmark: 2.1 },
                          { month: '2月', return: 8.7, benchmark: 3.5 },
                          { month: '3月', return: 12.1, benchmark: 4.2 },
                          { month: '4月', return: 15.8, benchmark: 5.8 },
                          { month: '5月', return: 18.3, benchmark: 6.9 },
                          { month: '6月', return: 20.5, benchmark: 7.2 },
                        ]}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                          <XAxis dataKey="month" stroke="#9ca3af" />
                          <YAxis stroke="#9ca3af" />
                          <Tooltip />
                          <Legend />
                          <Line type="monotone" dataKey="return" stroke="#10b981" strokeWidth={2} name="策略收益" />
                          <Line type="monotone" dataKey="benchmark" stroke="#6b7280" strokeWidth={2} name="基准收益" />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>

                    <div className="card">
                      <h3 className="text-lg font-semibold text-ocean-primary mb-4">回撤分析</h3>
                      <ResponsiveContainer width="100%" height={300}>
                        <BarChart data={[
                          { month: '1月', drawdown: -2.1 },
                          { month: '2月', drawdown: -1.8 },
                          { month: '3月', drawdown: -5.2 },
                          { month: '4月', drawdown: -3.7 },
                          { month: '5月', drawdown: -8.9 },
                          { month: '6月', drawdown: -12.3 },
                        ]}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                          <XAxis dataKey="month" stroke="#9ca3af" />
                          <YAxis stroke="#9ca3af" />
                          <Tooltip />
                          <Bar dataKey="drawdown" fill="#ef4444" />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>

                    <div className="card">
                      <h3 className="text-lg font-semibold text-ocean-primary mb-4">交易分布</h3>
                      <ResponsiveContainer width="100%" height={300}>
                        <PieChart>
                          <Pie
                            data={[
                              { name: '盈利交易', value: 12, color: '#10b981' },
                              { name: '亏损交易', value: 8, color: '#ef4444' },
                            ]}
                            cx="50%"
                            cy="50%"
                            labelLine={false}
                            label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                            outerRadius={80}
                            fill="#8884d8"
                            dataKey="value"
                          >
                            {[
                              { name: '盈利交易', value: 12, color: '#10b981' },
                              { name: '亏损交易', value: 8, color: '#ef4444' },
                            ].map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={entry.color} />
                            ))}
                          </Pie>
                          <Tooltip />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>

                    <div className="card">
                      <h3 className="text-lg font-semibold text-ocean-primary mb-4">月度表现</h3>
                      <ResponsiveContainer width="100%" height={300}>
                        <BarChart data={[
                          { month: '1月', trades: 3, winRate: 66.7 },
                          { month: '2月', trades: 4, winRate: 75.0 },
                          { month: '3月', trades: 2, winRate: 50.0 },
                          { month: '4月', trades: 5, winRate: 80.0 },
                          { month: '5月', trades: 3, winRate: 66.7 },
                          { month: '6月', trades: 3, winRate: 66.7 },
                        ]}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                          <XAxis dataKey="month" stroke="#9ca3af" />
                          <YAxis stroke="#9ca3af" />
                          <Tooltip />
                          <Legend />
                          <Bar dataKey="trades" fill="#8b5cf6" name="交易次数" />
                          <Line type="monotone" dataKey="winRate" stroke="#f59e0b" strokeWidth={2} name="胜率 (%)" />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                </>
              ) : (
                <div className="text-center py-12">
                  <BarChart3 className="w-16 h-16 text-white/30 mx-auto mb-4" />
                  <h3 className="text-xl font-medium text-ocean-primary mb-2">暂无回测结果</h3>
                  <p className="text-ocean-secondary mb-6">开始您的第一次回测</p>
                  <button className="btn-primary flex items-center mx-auto">
                    <Plus className="w-4 h-4 mr-2" />
                    新建回测
                  </button>
                </div>
              )}
            </div>
          </div>
        )

      case 'manual':
        return (
          <div className="space-y-6">
            <div className="card">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-semibold text-ocean-primary">手动测试交易记录</h3>
                <div className="flex space-x-3">
                  <button className="btn-secondary flex items-center">
                    <Download className="w-4 h-4 mr-2" />
                    导出CSV
                  </button>
                  <button className="btn-secondary flex items-center">
                    <FileText className="w-4 h-4 mr-2" />
                    导出PDF
                  </button>
                  <button 
                    onClick={() => navigate(`/demo/manual-test/${strategyId}`)}
                    className="btn-primary flex items-center"
                  >
                    <Target className="w-4 h-4 mr-2" />
                    管理测试
                  </button>
                </div>
              </div>

              {/* 交易记录表格 */}
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-white/5">
                    <tr>
                      <th className="text-left p-4 text-ocean-secondary font-medium">时间</th>
                      <th className="text-left p-4 text-ocean-secondary font-medium">方向</th>
                      <th className="text-left p-4 text-ocean-secondary font-medium">入场价格</th>
                      <th className="text-left p-4 text-ocean-secondary font-medium">出场价格</th>
                      <th className="text-left p-4 text-ocean-secondary font-medium">数量</th>
                      <th className="text-left p-4 text-ocean-secondary font-medium">盈亏</th>
                      <th className="text-left p-4 text-ocean-secondary font-medium">R值</th>
                      <th className="text-left p-4 text-ocean-secondary font-medium">操作</th>
                    </tr>
                  </thead>
                  <tbody>
                    {mockManualTestTrades
                      .filter(trade => trade.strategy_id === strategyId)
                      .slice(0, 10)
                      .map((trade) => (
                      <tr key={trade.id} className="border-b border-white/5 hover:bg-white/5 transition-colors">
                        <td className="p-4">
                          <div className="text-ocean-primary text-sm">
                            {new Date(trade.entry_time).toLocaleDateString()}
                          </div>
                          <div className="text-ocean-secondary text-xs">
                            {new Date(trade.entry_time).toLocaleTimeString()}
                          </div>
                        </td>
                        <td className="p-4">
                          <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                            trade.direction === 'long' 
                              ? 'bg-green-500/20 text-green-400 border border-green-500/30' 
                              : 'bg-red-500/20 text-red-400 border border-red-500/30'
                          }`}>
                            {trade.direction === 'long' ? '做多' : '做空'}
                          </span>
                        </td>
                        <td className="p-4">
                          <div className="text-ocean-primary font-medium">${trade.entry_price.toLocaleString()}</div>
                        </td>
                        <td className="p-4">
                          <div className="text-ocean-primary font-medium">${trade.exit_price.toLocaleString()}</div>
                        </td>
                        <td className="p-4">
                          <div className="text-ocean-primary font-medium">{trade.size}</div>
                        </td>
                        <td className={`p-4 font-semibold ${
                          trade.pnl_amount > 0 ? 'text-green-400' : 'text-red-400'
                        }`}>
                          ${trade.pnl_amount.toLocaleString()}
                        </td>
                        <td className={`p-4 font-semibold ${
                          trade.pnl_r > 0 ? 'text-green-400' : 'text-red-400'
                        }`}>
                          {trade.pnl_r.toFixed(2)}
                        </td>
                        <td className="p-4">
                          <div className="flex items-center space-x-2">
                            <button className="text-violet-300 hover:text-violet-400 p-1">
                              <Eye className="w-4 h-4" />
                            </button>
                            <button className="text-green-300 hover:text-green-400 p-1">
                              <Edit className="w-4 h-4" />
                            </button>
                            <button className="text-red-300 hover:text-red-400 p-1">
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {mockManualTestTrades.filter(trade => trade.strategy_id === strategyId).length === 0 && (
                <div className="text-center py-12">
                  <Target className="w-16 h-16 text-white/30 mx-auto mb-4" />
                  <h3 className="text-xl font-medium text-ocean-primary mb-2">暂无交易记录</h3>
                  <p className="text-ocean-secondary mb-6">开始记录您的第一笔手动测试交易</p>
                  <button className="btn-primary flex items-center mx-auto">
                    <Plus className="w-4 h-4 mr-2" />
                    添加交易记录
                  </button>
                </div>
              )}
            </div>
          </div>
        )

      case 'notes':
        return (
          <div className="space-y-6">
            <div className="card">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-semibold text-ocean-primary">研究笔记</h3>
                <button className="btn-primary flex items-center">
                  <Plus className="w-4 h-4 mr-2" />
                  添加笔记
                </button>
              </div>
              
              <div className="space-y-4">
                {mockNotes.filter(n => n.strategy_id === strategyId).map((note) => (
                  <div key={note.id} className="border border-white/20 rounded-xl p-4">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="text-ocean-primary font-medium">{note.title}</h4>
                      <span className="text-sm text-ocean-secondary">
                        {new Date(note.created_at).toLocaleDateString()}
                      </span>
                    </div>
                    <p className="text-ocean-secondary">{note.content}</p>
                  </div>
                ))}
              </div>

              {mockNotes.filter(n => n.strategy_id === strategyId).length === 0 && (
                <div className="text-center py-12">
                  <FileText className="w-16 h-16 text-white/30 mx-auto mb-4" />
                  <h3 className="text-xl font-medium text-ocean-primary mb-2">暂无研究笔记</h3>
                  <p className="text-ocean-secondary mb-6">开始记录您的研究发现和思考</p>
                  <button className="btn-primary flex items-center mx-auto">
                    <Plus className="w-4 h-4 mr-2" />
                    添加笔记
                  </button>
                </div>
              )}
            </div>
          </div>
        )

      case 'live':
        return (
          <div className="space-y-6">
            <div className="card">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-semibold text-ocean-primary">实盘追踪</h3>
                <button className="btn-primary flex items-center">
                  <Plus className="w-4 h-4 mr-2" />
                  添加追踪
                </button>
              </div>
              
              <div className="space-y-4">
                {mockLiveTracking.filter(l => l.strategy_id === strategyId).map((tracking) => (
                  <div key={tracking.id} className="border border-white/20 rounded-xl p-4">
                    <div className="flex items-center justify-between mb-4">
                      <h4 className="text-ocean-primary font-medium">{tracking.platform}</h4>
                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                        tracking.status === 'active' ? 'bg-green-500/20 text-green-400 border border-green-500/30' :
                        'bg-gray-500/20 text-gray-400 border border-gray-500/30'
                      }`}>
                        {tracking.status === 'active' ? '进行中' : '已完成'}
                      </span>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                      <div>
                        <div className="text-ocean-secondary">实际收益率</div>
                        <div className="text-ocean-primary font-semibold">{tracking.actual_return?.toFixed(2)}%</div>
                      </div>
                      <div>
                        <div className="text-ocean-secondary">实际回撤</div>
                        <div className="text-ocean-primary font-semibold">{tracking.actual_drawdown?.toFixed(2)}%</div>
                      </div>
                      <div>
                        <div className="text-ocean-secondary">交易次数</div>
                        <div className="text-ocean-primary font-semibold">{tracking.actual_trade_count}</div>
                      </div>
                      <div>
                        <div className="text-ocean-secondary">平台</div>
                        <div className="text-ocean-primary font-semibold">{tracking.platform}</div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {mockLiveTracking.filter(l => l.strategy_id === strategyId).length === 0 && (
                <div className="text-center py-12">
                  <Activity className="w-16 h-16 text-white/30 mx-auto mb-4" />
                  <h3 className="text-xl font-medium text-ocean-primary mb-2">暂无实盘追踪</h3>
                  <p className="text-ocean-secondary mb-6">开始追踪您的实盘交易表现</p>
                  <button className="btn-primary flex items-center mx-auto">
                    <Plus className="w-4 h-4 mr-2" />
                    添加追踪
                  </button>
                </div>
              )}
            </div>
          </div>
        )

      default:
        return null
    }
  }

  return (
    <div className="min-h-screen animate-fadein">
      <Navigation />
      {/* 主要内容 */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* 标签页 */}
        <div className="card">
          {/* 策略头部 */}
          <div className="border-b border-white/20 pb-6 mb-6">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-3xl font-bold text-gradient mb-2">
                  {strategy.name}
                </h1>
                <div className="flex items-center space-x-4 text-sm text-ocean-secondary">
                  <span className="flex items-center">
                    <TrendingUp className="w-4 h-4 mr-1" />
                    {strategy.type}
                  </span>
                  <span className="flex items-center">
                    <Calendar className="w-4 h-4 mr-1" />
                    创建于 {new Date(strategy.created_at).toLocaleDateString('zh-CN')}
                  </span>
                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(strategy.status)}`}>
                    {getStatusLabel(strategy.status)}
                  </span>
                </div>
              </div>
            </div>
          </div>
          <div className="border-b border-white/20">
            <div className="flex overflow-x-auto">
              {tabs.map((tab) => {
                const Icon = tab.icon
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex items-center px-6 py-4 text-sm font-medium transition-all duration-200 whitespace-nowrap ${
                      activeTab === tab.id
                        ? 'text-ocean-primary border-b-2 border-violet-500 bg-white/5'
                        : 'text-ocean-secondary hover:text-ocean-primary hover:bg-white/5'
                    }`}
                  >
                    <Icon className="w-4 h-4 mr-2" />
                    {tab.name}
                  </button>
                )
              })}
            </div>
          </div>
          
          <div className="p-6">
            {renderTabContent()}
          </div>
        </div>
      </main>
    </div>
  )
} 